import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  MessageCircle, Send, Inbox, Mail, MailOpen, AlertTriangle, Info, Megaphone,
  Trash2, CheckCheck, X, Clock, User, Users, ChevronRight, RefreshCw, Plus, Filter,
  Paperclip, Image, FileText, Download, Volume2, VolumeX
} from 'lucide-react';
import { toast } from "sonner";
import { API, BACKEND_URL } from "@/config";
import { useRoleFlags } from '@/auth/useRoleFlags';

// Mesaj API istekleri merkezi config üzerinden (port 8000; 8002 kullanılmaz)

// Sesli bildirim için Web Audio API
const playNotificationSound = (type = 'normal') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'urgent') {
      // Acil mesaj: Daha yüksek ve tekrarlı ses
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.frequency.setValueAtTime(988, audioContext.currentTime + 0.1); // B5
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.2); // A5
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } else {
      // Normal mesaj: Yumuşak ding sesi
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    }
  } catch (error) {
    console.log('Ses çalınamadı:', error);
  }
};

// Mesaj türü renkleri ve ikonları
// Ek dosya boyutu limiti. Kanonik kaynak: backend/shared/config/settings.py
// `STORAGE_MAX_BYTES` (10MB). nginx `client_max_body_size` da oradan türer.
// Frontend build-time'da backend config'ini okuyamaz → değer elle eşlenir.
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const MESSAGE_TYPES = {
  urgent: { label: 'Acil', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, bgColor: 'bg-red-50' },
  announcement: { label: 'Duyuru', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Megaphone, bgColor: 'bg-yellow-50' },
  info: { label: 'Bilgilendirme', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Info, bgColor: 'bg-blue-50' }
};

// Rol isimleri
const ROLE_LABELS = {
  admin: 'Müdür',
  mudur_yardimcisi: 'Müdür Yardımcısı',
  bolge_muhendisi: 'Bölge Mühendisi',
  formen: 'Formen',
  park_gorevlisi: 'Park Görevlisi',
  cim_ekibi: 'Çim Ekibi'
};

export default function MessagesTab({ 
  userRole, 
  personnelId, 
  personnelName,
  regions = [],
  parks = [],
  personnel = [],
  onUnreadCountRefresh
}) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [activeTab, setActiveTab] = useState('inbox');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Filtreleme state'leri
  const [inboxTypeFilter, setInboxTypeFilter] = useState('all');
  const [inboxSenderRoleFilter, setInboxSenderRoleFilter] = useState('all');
  const [sentTypeFilter, setSentTypeFilter] = useState('all');
  const [sentRecipientRoleFilter, setSentRecipientRoleFilter] = useState('all'); // Gönderilenler için alıcı filtresi
  
  // Mesaj gönderme formu
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [composeForm, setComposeForm] = useState({
    title: '',
    content: '',
    message_type: 'info',
    target_roles: [],
    target_region_ids: [],
    target_park_ids: [],
    attachments: []
  });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: null, // 'inbox' | 'sent'
    message: null,
    text: ''
  });
  
  // Sesli bildirim ayarları
  const [soundEnabled, setSoundEnabled] = useState(true);
  const previousUnreadCount = useRef(0);
  const fileInputRef = useRef(null);

  // Kullanıcının bölgelerini al
  const getUserRegions = useCallback(() => {
    if (isMudur) {
      // Admin tüm bölgeleri görebilir
      return regions;
    }
    
    // Diğer kullanıcılar için kendi bölgelerini bul
    const currentUser = personnel.find(p => p.id === personnelId);
    if (!currentUser) return [];
    
    const userRegionIds = currentUser.region_ids || [];
    return regions.filter(r => userRegionIds.includes(r.id));
  }, [userRole, personnelId, personnel, regions]);

  // Gelen kutusu için: Bana mesaj gönderebilen ÜST roller (sadece üstlerim bana mesaj gönderebilir)
  const getInboxSenderRoles = useCallback(() => {
    switch (userRole) {
      case 'mudur':
        return []; // Müdür'e kimse mesaj gönderemez
      case 'mudur_yardimcisi':
        return ['mudur']; // Sadece Müdür gönderebilir
      case 'bolge_muhendisi':
        return ['mudur', 'mudur_yardimcisi']; // Müdür ve Müdür Yardımcısı
      case 'formen':
        return ['mudur', 'mudur_yardimcisi', 'bolge_muhendisi']; // Üst 3 rol
      case 'park_gorevlisi':
      case 'cim_ekibi':
        return ['mudur', 'mudur_yardimcisi', 'bolge_muhendisi', 'formen']; // Herkesten mesaj alabilir
      default:
        return [];
    }
  }, [userRole]);

  // Gönderilenler için: Mesaj gönderebildiğim ALT roller
  // NOT (Migration 0064): "cim_ekibi" rolü kaldırıldı; personel park_gorevlisi'ye
  // taşındı. Backend send_message hiyerarşisinde de yok — listede tutulursa
  // seçildiğinde 403 "Bu role mesaj gönderme yetkiniz yok" hatası veriyordu.
  const getAllowedRoles = useCallback(() => {
    switch (userRole) {
      case 'mudur':
        return ['mudur_yardimcisi', 'bolge_muhendisi', 'formen', 'park_gorevlisi'];
      case 'mudur_yardimcisi':
        return ['bolge_muhendisi', 'formen', 'park_gorevlisi'];
      case 'bolge_muhendisi':
        return ['formen', 'park_gorevlisi'];
      case 'formen':
        return ['park_gorevlisi'];
      default:
        return [];
    }
  }, [userRole]);

  // Mesajları yükle (silent: true ise arka planda sessiz güncelleme, sayfa yenilenmiş gibi görünmez)
  const fetchMessages = useCallback(async (silent = false) => {
    if (!personnelId) return;
    
    if (!silent) setLoading(true);
    try {
      const [inboxRes, sentRes, unreadRes] = await Promise.all([
        axios.get(`${API}/api/messages/inbox/${personnelId}`),
        axios.get(`${API}/api/messages/sent/${personnelId}`),
        axios.get(`${API}/api/messages/unread-count/${personnelId}`)
      ]);
      
      setInboxMessages(Array.isArray(inboxRes.data) ? inboxRes.data : []);
      setSentMessages(Array.isArray(sentRes.data) ? sentRes.data : []);
      
      const newUnreadCount = unreadRes.data?.unread_count ?? 0;
      
      // Yeni mesaj geldi mi kontrol et ve ses çal (sadece sessiz olmayan yüklemede)
      if (!silent && soundEnabled && newUnreadCount > previousUnreadCount.current && previousUnreadCount.current >= 0) {
        const newMessages = (inboxRes.data || []).filter(msg => !msg.is_read);
        const hasUrgent = newMessages.some(msg => msg.message_type === 'urgent');
        playNotificationSound(hasUrgent ? 'urgent' : 'normal');
      }
      
      previousUnreadCount.current = newUnreadCount;
      setUnreadCount(newUnreadCount);
      onUnreadCountRefresh?.();
    } catch (error) {
      if (!silent) console.error('Mesajlar yüklenemedi:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [personnelId, soundEnabled, onUnreadCountRefresh]);

  useEffect(() => {
    fetchMessages(false); // İlk yüklemede loading göster
    // Arka planda 60 saniyede bir sessiz güncelle (sayfa titremesi/ yenilenme hissi olmaz)
    const interval = setInterval(() => fetchMessages(true), 60000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    const handler = () => fetchMessages(true);
    window.addEventListener('messagesRefresh', handler);
    return () => window.removeEventListener('messagesRefresh', handler);
  }, [fetchMessages]);

  // Mesajı okundu olarak işaretle
  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/api/messages/${messageId}/read?personnel_id=${personnelId}`);
      setInboxMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      previousUnreadCount.current = Math.max(0, previousUnreadCount.current - 1);
      onUnreadCountRefresh?.();
    } catch (error) {
      console.error('Okundu işaretleme hatası:', error);
    }
  };

  // Tüm mesajları okundu olarak işaretle
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/api/messages/mark-all-read/${personnelId}`);
      setInboxMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
      setUnreadCount(0);
      previousUnreadCount.current = 0;
      onUnreadCountRefresh?.();
      toast.success('Tüm mesajlar okundu olarak işaretlendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  // Dosya yükleme
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [...composeForm.attachments];
    
    for (const file of files) {
      // Dosya boyutu kontrolü — 10MB.
      // Kanonik kaynak backend/shared/config/settings.py `STORAGE_MAX_BYTES`
      // (10MB, ek/evrak limiti); nginx `client_max_body_size` de oradan türer.
      // Bu yalnız erken bir kullanıcı geri bildirimidir — GÜVENLİK KONTROLÜ
      // DEĞİL; gerçek sınır backend'de uygulanır (413). Settings'teki değer
      // değişirse burası da elle güncellenmeli (frontend build-time'da backend
      // config'ini okumaz).
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name} ${MAX_ATTACHMENT_BYTES / (1024 * 1024)}MB'dan büyük olamaz`);
        continue;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API}/api/messages/upload-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newAttachments.push(response.data);
        toast.success(`${file.name} yüklendi`);
      } catch (error) {
        toast.error(`${file.name} yüklenemedi: ${error.response?.data?.detail || 'Hata'}`);
      }
    }
    
    setComposeForm(prev => ({ ...prev, attachments: newAttachments }));
    setUploading(false);
    
    // Input'u temizle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Ek dosyayı kaldır
  const removeAttachment = async (fileId) => {
    try {
      await axios.delete(`${API}/api/messages/attachment/${fileId}`);
      setComposeForm(prev => ({
        ...prev,
        attachments: prev.attachments.filter(a => a.file_id !== fileId)
      }));
      toast.success('Dosya kaldırıldı');
    } catch (error) {
      toast.error('Dosya kaldırılamadı');
    }
  };

  // Mesaj gönder
  const handleSendMessage = async () => {
    if (!composeForm.title.trim() || !composeForm.content.trim()) {
      toast.error('Başlık ve içerik zorunludur');
      return;
    }
    
    if (composeForm.target_roles.length === 0) {
      toast.error('En az bir alıcı grubu seçmelisiniz');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/api/messages?sender_id=${personnelId}&sender_role=${userRole}`, composeForm);
      toast.success(`Mesaj ${response.data.recipient_count} kişiye gönderildi`);
      setShowComposeDialog(false);
      setComposeForm({
        title: '',
        content: '',
        message_type: 'info',
        target_roles: [],
        target_region_ids: [],
        target_park_ids: [],
        attachments: []
      });
      fetchMessages();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || detail.map(d => d.msg).join(', ') || 'Mesaj gönderilemedi')
        : (detail || error.message || 'Mesaj gönderilemedi');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Gönderen mesaj silme (gönderilen kutusundan)
  const handleDeleteSentMessage = async (messageOrId, skipConfirm = false) => {
    const messageObj = (messageOrId && typeof messageOrId === 'object') ? messageOrId : null;
    const messageId = messageObj ? messageObj.id : messageOrId;
    if (!skipConfirm) {
      setDeleteConfirm({
        open: true,
        type: 'sent',
        message: messageObj || { id: messageId },
        text: 'Bu mesajı tüm alıcı panellerinden kalıcı olarak silmek istediğinize emin misiniz?'
      });
      return;
    }
    try {
      await axios.delete(`${API}/api/messages/${messageId}?personnel_id=${personnelId}`);
      setSentMessages(prev => prev.filter(msg => msg.id !== messageId));
      toast.success('Mesaj silindi');
      setSelectedMessage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesaj silinemedi');
    }
  };

  // Alıcı mesaj silme (gelen kutusundan) - PG vb. sadece kendi panelinde kendi kutusundan kaldırır (okunduktan sonra)
  const handleDeleteInboxMessage = async (messageOrId, skipConfirm = false) => {
    const messageObj = (messageOrId && typeof messageOrId === 'object') ? messageOrId : null;
    const id = messageObj
      ? String(messageObj.id || '').trim()
      : (messageOrId != null ? String(messageOrId).trim() : '');
    if (!id || !personnelId) {
      toast.error('Mesaj veya oturum bilgisi eksik');
      return;
    }
    const isRead = messageObj?.is_read ?? inboxMessages.find(m => String(m?.id || '').trim() === id)?.is_read;
    if (isParkGorevlisi && !isRead) {
      toast.error('Mesajı silmeden önce okumalısınız.');
      return;
    }
    if (!skipConfirm) {
      setDeleteConfirm({
        open: true,
        type: 'inbox',
        message: messageObj || { id },
        text: 'Bu mesajı kutunuzdan silmek istediğinize emin misiniz?'
      });
      return;
    }
    try {
      const url = `${API}/api/messages/${id}/inbox?personnel_id=${encodeURIComponent(personnelId)}`;
      await axios.delete(url, { timeout: 15000 });
      setInboxMessages(prev => prev.filter(msg => String(msg?.id || '').trim() !== id));
      setSelectedMessage(null);
      onUnreadCountRefresh?.();
      toast.success('Mesaj kutunuzdan silindi');
      fetchMessages(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? (detail[0]?.msg || 'Mesaj silinemedi') : (detail || 'Mesaj silinemedi');
      toast.error(msg);
    }
  };

  // Rol checkbox değişikliği
  const handleRoleChange = (role, checked) => {
    setComposeForm(prev => ({
      ...prev,
      target_roles: checked 
        ? [...prev.target_roles, role]
        : prev.target_roles.filter(r => r !== role)
    }));
  };

  // Tarih formatla
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Az önce';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} saat önce`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} gün önce`;
    
    return date.toLocaleDateString('tr-TR');
  };

  // Gelen kutusu için gösterilecek üst roller
  const inboxSenderRoles = getInboxSenderRoles();
  
  // Gönderilenler için gösterilecek alt roller
  const allowedRoles = getAllowedRoles();
  // isParkGorevlisi useRoleFlags'ten geliyor (yukarıda)

  // Filtrelenmiş gelen kutusu mesajları
  const filteredInboxMessages = useMemo(() => {
    return inboxMessages.filter(msg => {
      // Mesaj türü filtresi
      if (inboxTypeFilter !== 'all' && msg.message_type !== inboxTypeFilter) {
        return false;
      }
      // Gönderen rolü filtresi
      if (inboxSenderRoleFilter !== 'all' && msg.sender_role !== inboxSenderRoleFilter) {
        return false;
      }
      return true;
    });
  }, [inboxMessages, inboxTypeFilter, inboxSenderRoleFilter]);

  // Filtrelenmiş gönderilen mesajlar
  const filteredSentMessages = useMemo(() => {
    return sentMessages.filter(msg => {
      // Mesaj türü filtresi
      if (sentTypeFilter !== 'all' && msg.message_type !== sentTypeFilter) {
        return false;
      }
      // Alıcı rolü filtresi (target_roles içinde var mı kontrol et)
      if (sentRecipientRoleFilter !== 'all') {
        const targetRoles = msg.target_roles || [];
        if (!targetRoles.includes(sentRecipientRoleFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [sentMessages, sentTypeFilter, sentRecipientRoleFilter]);

  const canSendMessages = allowedRoles.length > 0;
  const userRegions = getUserRegions();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold">Mesajlar</h2>
            <p className="text-gray-500">Gelen ve gönderilen mesajlarınız</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Ses açma/kapama butonu */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              if (!soundEnabled) {
                playNotificationSound('normal'); // Test sesi çal
              }
            }}
            title={soundEnabled ? 'Bildirimleri sessize al' : 'Bildirim sesini aç'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button variant="outline" onClick={() => fetchMessages(false)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          {canSendMessages && (
            <Button onClick={() => setShowComposeDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Yeni Mesaj
            </Button>
          )}
        </div>
      </div>

      {/* Ana İçerik */}
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="px-6 pt-4 border-b">
              <TabsList className={`grid w-full max-w-md ${canSendMessages ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <TabsTrigger value="inbox" className="flex items-center gap-2">
                  <Inbox className="h-4 w-4" />
                  Gelen Kutusu
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white ml-1">{unreadCount}</Badge>
                  )}
                </TabsTrigger>
                {canSendMessages && (
                  <TabsTrigger value="sent" className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Gönderilenler
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Gelen Kutusu */}
            <TabsContent value="inbox" className="mt-0">
              {/* Filtreler */}
              <div className="flex items-center gap-4 px-6 py-3 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Filtrele:</span>
                </div>
                <SearchableSelect
                  value={inboxTypeFilter}
                  onChange={setInboxTypeFilter}
                  className="w-[150px] h-8 text-sm"
                  allLabel="Tüm Türler"
                  allValue="all"
                  searchPlaceholder="Tür ara..."
                  options={[
                    { id: 'urgent', name: '🔴 Acil' },
                    { id: 'announcement', name: '🟡 Duyuru' },
                    { id: 'info', name: '🔵 Bilgilendirme' },
                  ]}
                />
                {/* Gönderen filtresi - Sadece bana mesaj gönderebilen üst roller */}
                {inboxSenderRoles.length > 0 && (
                  <SearchableSelect
                    value={inboxSenderRoleFilter}
                    onChange={setInboxSenderRoleFilter}
                    className="w-[180px] h-8 text-sm"
                    allLabel="Tüm Üst Birimler"
                    allValue="all"
                    searchPlaceholder="Gönderen ara..."
                    options={inboxSenderRoles.map(role => ({ id: role, name: ROLE_LABELS[role] }))}
                  />
                )}
                {(inboxTypeFilter !== 'all' || inboxSenderRoleFilter !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setInboxTypeFilter('all'); setInboxSenderRoleFilter('all'); }}
                    className="text-xs"
                  >
                    Filtreleri Temizle
                  </Button>
                )}
                <div className="ml-auto">
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                      <CheckCheck className="h-4 w-4 mr-1" />
                      Tümünü Okundu İşaretle
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="min-h-[400px]">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : filteredInboxMessages.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <Inbox className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">{inboxMessages.length === 0 ? 'Henüz mesajınız yok' : 'Filtreye uygun mesaj yok'}</p>
                    <p className="text-sm">{inboxMessages.length === 0 ? 'Yeni mesajlar burada görünecek' : 'Filtreleri değiştirmeyi deneyin'}</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredInboxMessages.map((msg) => {
                      const typeInfo = MESSAGE_TYPES[msg.message_type] || MESSAGE_TYPES.info;
                      const TypeIcon = typeInfo.icon;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                            !msg.is_read ? `${typeInfo.bgColor} border-l-4 ${typeInfo.color.split(' ')[2]}` : ''
                          }`}
                          onClick={() => {
                            setSelectedMessage({ ...msg, isInbox: true });
                            if (!msg.is_read) markAsRead(msg.id);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <TypeIcon className={`h-5 w-5 mt-1 flex-shrink-0 ${msg.is_read ? 'text-gray-400' : typeInfo.color.split(' ')[1]}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`font-semibold truncate ${msg.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                                    {msg.title}
                                  </span>
                                  <Badge className={`${typeInfo.color} text-xs`}>{typeInfo.label}</Badge>
                                  {msg.sender_role && (
                                    <Badge variant="outline" className="text-xs">{ROLE_LABELS[msg.sender_role] || msg.sender_role}</Badge>
                                  )}
                                  {msg.attachments?.length > 0 && (
                                    <span className="flex items-center gap-1 text-gray-400" title={`${msg.attachments.length} ek`}>
                                      <Paperclip className="h-3 w-3" />
                                      <span className="text-xs">{msg.attachments.length}</span>
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {msg.sender_name} • {formatDate(msg.created_at)}
                                </p>
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{msg.content}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {!msg.is_read && (
                                <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 ${isParkGorevlisi && !msg.is_read ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-500'}`}
                                title={isParkGorevlisi && !msg.is_read ? 'Önce mesajı okuyun' : 'Kutumdan sil'}
                                disabled={isParkGorevlisi && !msg.is_read}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteInboxMessage(msg);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Gönderilenler */}
            <TabsContent value="sent" className="mt-0">
              {/* Filtreler */}
              <div className="flex items-center gap-4 px-6 py-3 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Filtrele:</span>
                </div>
                <SearchableSelect
                  value={sentTypeFilter}
                  onChange={setSentTypeFilter}
                  className="w-[150px] h-8 text-sm"
                  allLabel="Tüm Türler"
                  allValue="all"
                  searchPlaceholder="Tür ara..."
                  options={[
                    { id: 'urgent', name: '🔴 Acil' },
                    { id: 'announcement', name: '🟡 Duyuru' },
                    { id: 'info', name: '🔵 Bilgilendirme' },
                  ]}
                />
                {/* Alıcı filtresi - Mesaj gönderebildiğim alt roller */}
                {allowedRoles.length > 0 && (
                  <SearchableSelect
                    value={sentRecipientRoleFilter}
                    onChange={setSentRecipientRoleFilter}
                    className="w-[180px] h-8 text-sm"
                    allLabel="Tüm Alt Birimler"
                    allValue="all"
                    searchPlaceholder="Alıcı grubu ara..."
                    options={allowedRoles.map(role => ({ id: role, name: ROLE_LABELS[role] }))}
                  />
                )}
                {(sentTypeFilter !== 'all' || sentRecipientRoleFilter !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setSentTypeFilter('all'); setSentRecipientRoleFilter('all'); }}
                    className="text-xs"
                  >
                    Filtreleri Temizle
                  </Button>
                )}
              </div>

              <div className="min-h-[400px]">
                {filteredSentMessages.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <Send className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">{sentMessages.length === 0 ? 'Henüz gönderilen mesajınız yok' : 'Filtreye uygun mesaj yok'}</p>
                    {canSendMessages && sentMessages.length === 0 && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setShowComposeDialog(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        İlk Mesajınızı Gönderin
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredSentMessages.map((msg) => {
                      const typeInfo = MESSAGE_TYPES[msg.message_type] || MESSAGE_TYPES.info;
                      const TypeIcon = typeInfo.icon;
                      const readCount = msg.read_count || 0;
                      const msgUnreadCount = msg.unread_count || 0;
                      const totalCount = readCount + msgUnreadCount;
                      const readPercent = totalCount > 0 ? Math.round((readCount / totalCount) * 100) : 0;
                      
                      return (
                        <div
                          key={msg.id}
                          className="p-4 cursor-pointer transition-colors hover:bg-gray-50"
                          onClick={() => setSelectedMessage({ ...msg, isSent: true })}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <TypeIcon className={`h-5 w-5 mt-1 flex-shrink-0 ${typeInfo.color.split(' ')[1]}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-gray-700 truncate">{msg.title}</span>
                                  <Badge className={`${typeInfo.color} text-xs`}>{typeInfo.label}</Badge>
                                  {msg.attachments?.length > 0 && (
                                    <span className="flex items-center gap-1 text-gray-400" title={`${msg.attachments.length} ek`}>
                                      <Paperclip className="h-3 w-3" />
                                      <span className="text-xs">{msg.attachments.length}</span>
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {totalCount} kişiye • {formatDate(msg.created_at)}
                                </p>
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{msg.content}</p>
                                
                                {/* Okuma durumu progress bar */}
                                <div className="mt-2 flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-green-500 transition-all"
                                      style={{ width: `${readPercent}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                                    <span className="text-green-600">{readCount}</span>
                                    <span className="text-gray-400"> / </span>
                                    <span className="text-orange-600">{msgUnreadCount}</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-400 hover:text-red-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSentMessage(msg);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Mesaj Detay Dialog */}
      <DraggableModal
        isOpen={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        title={selectedMessage && (
          <div className="flex items-center gap-2">
            {(() => {
              const typeInfo = MESSAGE_TYPES[selectedMessage.message_type] || MESSAGE_TYPES.info;
              const TypeIcon = typeInfo.icon;
              return <TypeIcon className={`h-5 w-5 ${typeInfo.color.split(' ')[1]}`} />;
            })()}
            <span>{selectedMessage.title}</span>
          </div>
        )}
        subtitle={selectedMessage && (
          selectedMessage.isSent ? (
            `${selectedMessage.recipient_count} kişiye gönderildi • ${formatDate(selectedMessage.created_at)}`
          ) : (
            `${selectedMessage.sender_name} • ${formatDate(selectedMessage.created_at)}`
          )
        )}
        maxWidth="max-w-2xl"
      >
          {selectedMessage && (
            <>
              <div className="py-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>

              {/* Ekler */}
              {selectedMessage.attachments?.length > 0 && (
                <div className="py-3 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Paperclip className="h-4 w-4" />
                    Ekler ({selectedMessage.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedMessage.attachments.map((attachment, index) => (
                      <div key={attachment.file_id || index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {attachment.file_type === 'image' ? (
                            <Image className="h-4 w-4 text-blue-500" />
                          ) : (
                            <FileText className="h-4 w-4 text-orange-500" />
                          )}
                          <span className="text-sm">{attachment.filename}</span>
                        </div>
                        <a
                          href={`${BACKEND_URL}/api${attachment.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <Download className="h-4 w-4" />
                          İndir
                        </a>
                      </div>
                    ))}
                  </div>
                  {/* Resim önizleme */}
                  {selectedMessage.attachments.filter(a => a.file_type === 'image').length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {selectedMessage.attachments
                        .filter(a => a.file_type === 'image')
                        .map((img, index) => (
                          <a
                            key={img.file_id || index}
                            href={`${BACKEND_URL}/api${img.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={`${BACKEND_URL}/api${img.file_path}`}
                              alt={img.filename}
                              className="w-full h-24 object-cover rounded-lg border hover:opacity-90 transition-opacity"
                            />
                          </a>
                        ))}
                    </div>
                  )}
                </div>
              )}
              
              {selectedMessage.target_roles?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2 border-t">
                  <span className="text-xs text-gray-500">Alıcı Grupları:</span>
                  {selectedMessage.target_roles.map(role => (
                    <Badge key={role} variant="outline" className="text-xs">
                      {ROLE_LABELS[role] || role}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Gönderilen mesajlar için detaylı okuma durumu */}
              {selectedMessage.isSent && (
                <div className="mt-4 pt-4 border-t space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-700">Okuma Durumu</h4>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCheck className="h-4 w-4" />
                        {selectedMessage.read_count || 0} Okudu
                      </span>
                      <span className="flex items-center gap-1 text-orange-600">
                        <Clock className="h-4 w-4" />
                        {selectedMessage.unread_count || 0} Okumadı
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Okuyanlar */}
                    <div className="border rounded-lg p-3 bg-green-50">
                      <h5 className="font-medium text-green-700 mb-2 flex items-center gap-1">
                        <CheckCheck className="h-4 w-4" />
                        Okuyanlar ({selectedMessage.read_count || 0})
                      </h5>
                      <ScrollArea className="h-[150px]">
                        {selectedMessage.read_persons?.length > 0 ? (
                          <div className="space-y-1">
                            {selectedMessage.read_persons.map(person => (
                              <div key={person.id} className="text-sm flex items-center gap-2 py-1">
                                <User className="h-3 w-3 text-green-600" />
                                <span className="text-gray-700">{person.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Henüz kimse okumadı</p>
                        )}
                      </ScrollArea>
                    </div>
                    
                    {/* Okumayanlar */}
                    <div className="border rounded-lg p-3 bg-orange-50">
                      <h5 className="font-medium text-orange-700 mb-2 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Okumayanlar ({selectedMessage.unread_count || 0})
                      </h5>
                      <ScrollArea className="h-[150px]">
                        {selectedMessage.unread_persons?.length > 0 ? (
                          <div className="space-y-1">
                            {selectedMessage.unread_persons.map(person => (
                              <div key={person.id} className="text-sm flex items-center gap-2 py-1">
                                <User className="h-3 w-3 text-orange-600" />
                                <span className="text-gray-700">{person.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Herkes okudu</p>
                        )}
                      </ScrollArea>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                {selectedMessage.isSent ? (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteSentMessage(selectedMessage)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Mesajı Sil
                  </Button>
                ) : selectedMessage.isInbox ? (
                  <Button 
                    variant="destructive"
                    size="sm"
                    title={isParkGorevlisi && !selectedMessage.is_read ? 'Önce mesajı okuyun' : 'Mesajı kutumdan sil'}
                    disabled={isParkGorevlisi && !selectedMessage.is_read}
                    onClick={() => handleDeleteInboxMessage(selectedMessage)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Kutumdan Sil
                  </Button>
                ) : null}
              </div>
            </>
          )}
      </DraggableModal>

      {/* Silme Onay Modalı (tarayıcı popup yerine uygulama içi) */}
      <DraggableModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: null, message: null, text: '' })}
        title="Silme Onayı"
        subtitle="Bu işlem geri alınamaz."
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{deleteConfirm.text}</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm({ open: false, type: null, message: null, text: '' })}
            >
              Vazgeç
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                const pending = deleteConfirm;
                setDeleteConfirm({ open: false, type: null, message: null, text: '' });
                if (pending.type === 'inbox') {
                  await handleDeleteInboxMessage(pending.message, true);
                } else if (pending.type === 'sent') {
                  await handleDeleteSentMessage(pending.message, true);
                }
              }}
            >
              Evet, Sil
            </Button>
          </div>
        </div>
      </DraggableModal>

      {/* Yeni Mesaj Dialog */}
      <DraggableModal
        isOpen={showComposeDialog}
        onClose={() => setShowComposeDialog(false)}
        title="Yeni Mesaj Gönder"
        subtitle="Seçtiğiniz gruplara mesaj gönderin"
        maxWidth="max-w-lg"
      >
          <div className="space-y-4">
            {/* Mesaj Türü */}
            <div>
              <Label>Mesaj Türü</Label>
              <SearchableSelect
                value={composeForm.message_type}
                onChange={(val) => setComposeForm(prev => ({ ...prev, message_type: val }))}
                showAllOption={false}
                placeholder="Tür seçin"
                searchPlaceholder="Tür ara..."
                options={[
                  { id: "info", name: "Bilgilendirme", icon: <Info className="h-4 w-4 text-blue-500" /> },
                  { id: "urgent", name: "Acil", icon: <AlertTriangle className="h-4 w-4 text-red-500" /> },
                  { id: "announcement", name: "Duyuru", icon: <Megaphone className="h-4 w-4 text-yellow-500" /> },
                ]}
              />
            </div>

            {/* Alıcı Grupları */}
            <div>
              <Label className="mb-2 block">Alıcı Grupları</Label>
              <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50">
                {allowedRoles.map(role => (
                  <div key={role} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`role-${role}`}
                      checked={composeForm.target_roles.includes(role)}
                      onCheckedChange={(checked) => handleRoleChange(role, checked)}
                    />
                    <label htmlFor={`role-${role}`} className="text-sm cursor-pointer">
                      {ROLE_LABELS[role]}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                * Sadece size bağlı personellere gönderilir
              </p>
            </div>

            {/* Bölge Filtresi (Opsiyonel) - Sadece kullanıcının bölgeleri */}
            {userRegions.length > 0 && (
              <div>
                <Label>Bölge Filtresi (Opsiyonel)</Label>
                <SearchableSelect
                  value={composeForm.target_region_ids[0] || 'all'}
                  onChange={(val) => setComposeForm(prev => ({
                    ...prev,
                    target_region_ids: val === 'all' ? [] : [val]
                  }))}
                  allLabel="Tüm Bölgelerim"
                  allValue="all"
                  searchPlaceholder="Bölge ara..."
                  options={userRegions.map(region => ({ id: region.id, name: region.name }))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sadece kendi bölgeleriniz listelenir
                </p>
              </div>
            )}

            {/* Başlık */}
            <div>
              <Label>Başlık</Label>
              <Input 
                placeholder="Mesaj başlığı"
                value={composeForm.title}
                onChange={(e) => setComposeForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            {/* İçerik */}
            <div>
              <Label>Mesaj İçeriği</Label>
              <Textarea 
                placeholder="Mesajınızı yazın..."
                rows={4}
                value={composeForm.content}
                onChange={(e) => setComposeForm(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>

            {/* Dosya/Fotoğraf Ekleme */}
            <div>
              <Label>Dosya/Fotoğraf Ekle (Opsiyonel)</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <Paperclip className="h-4 w-4 mr-2" />
                      Dosya Seç (Max {MAX_ATTACHMENT_BYTES / (1024 * 1024)}MB)
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-1">
                  Desteklenen: JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX
                </p>
              </div>

              {/* Yüklenen dosyalar listesi */}
              {composeForm.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {composeForm.attachments.map((attachment, index) => (
                    <div 
                      key={attachment.file_id || index} 
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-2">
                        {attachment.file_type === 'image' ? (
                          <Image className="h-4 w-4 text-blue-500" />
                        ) : (
                          <FileText className="h-4 w-4 text-orange-500" />
                        )}
                        <span className="text-sm truncate max-w-[200px]">{attachment.filename}</span>
                        <span className="text-xs text-gray-400">
                          ({(attachment.file_size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-500 hover:text-red-700"
                        onClick={() => removeAttachment(attachment.file_id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowComposeDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleSendMessage} disabled={sending || uploading}>
              {sending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Gönder
                </>
              )}
            </Button>
          </div>
      </DraggableModal>
    </div>
  );
}
